import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { track } from "@/lib/analytics";
import { getState, type UsStateCode } from "@/lib/states";
import type { UserProfile } from "@/lib/profile-api";
import type { RuleDiffResponseDto } from "@/lib/rulediff-api";

/**
 * apps/web :: components/dashboard :: RuleDiffPanel
 *
 * The main panel on the dashboard. Auto-fetches a rule-diff whenever
 * `(profile, currentState)` changes and renders the result in one of four
 * states: loading, preparing (503), error (non-503), or ok.
 *
 * Dependency injection for the fetcher is intentional — the dashboard
 * owns the session cache, so we accept a fetcher callback rather than
 * calling `generateRuleDiffForProfile` directly. That keeps the panel
 * testable without a network mock and also lets the cache transparently
 * short-circuit the "switch-back" path.
 *
 * ADR-0010 forward-compat: the response shape today is
 * `{diff, providerId, latencyMs, tokenEstimate?}`. Post-ADR-0010 a
 * `status: "pending_review"` field may appear. We destructure only the
 * fields we render, so extra keys flow through harmlessly.
 */

/** Fetcher contract. Callers (the page) derive the actual call via the
 *  session cache + `generateRuleDiffForProfile`. */
export type RuleDiffFetcher = (
  profile: UserProfile,
  currentState: UsStateCode,
) => Promise<RuleDiffResponseDto>;

export interface RuleDiffPanelProps {
  profile: UserProfile;
  currentState: UsStateCode;
  fetcher: RuleDiffFetcher;
}

type PanelState =
  | { kind: "loading" }
  | { kind: "ok"; response: RuleDiffResponseDto }
  | { kind: "preparing" } // 503 — backend not configured for this request yet
  | { kind: "error"; message: string };

/** Duck-type an error with a numeric `status` (our `ApiError`s carry one). */
function statusOf(err: unknown): number | undefined {
  if (err && typeof err === "object" && "status" in err) {
    const s = (err as { status: unknown }).status;
    if (typeof s === "number") return s;
  }
  return undefined;
}

function messageOf(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err);
}

export default function RuleDiffPanel({
  profile,
  currentState,
  fetcher,
}: RuleDiffPanelProps) {
  const { t } = useTranslation();
  const [state, setState] = useState<PanelState>({ kind: "loading" });
  // `nonce` is bumped to manually re-trigger a fetch — used by the Retry
  // button. Changing it re-runs the effect without needing to touch
  // `currentState` or mount-unmount the panel.
  const [nonce, setNonce] = useState(0);
  // Analytics: fire `rule_diff.panel_viewed` once per DISTINCT state the
  // user actually sees successfully. Refetches (Retry click) MUST NOT
  // double-emit; switching tabs MUST emit fresh. Tracked via a ref set
  // of observed states.
  const observedStatesRef = useRef<Set<UsStateCode>>(new Set());

  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    (async () => {
      try {
        const response = await fetcher(profile, currentState);
        if (cancelled) return;
        setState({ kind: "ok", response });
        // Emit only on success, and only the first time we successfully
        // show this state. StrictMode's double-effect is harmless because
        // the ref is set on the first pass.
        if (!observedStatesRef.current.has(currentState)) {
          observedStatesRef.current.add(currentState);
          track("rule_diff.panel_viewed", {
            state: currentState,
            locale: profile.native_language,
          });
        }
      } catch (err) {
        if (cancelled) return;
        const status = statusOf(err);
        if (status === 503) {
          setState({ kind: "preparing" });
          return;
        }
        setState({ kind: "error", message: messageOf(err) });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [profile, currentState, fetcher, nonce]);

  const retry = useCallback(() => setNonce((n) => n + 1), []);

  const stateEntry = getState(currentState);
  const stateLabel = t(`state.${currentState.toLowerCase()}.name`, {
    defaultValue: stateEntry?.name ?? currentState,
  });

  return (
    <section
      id="dashboard-rule-diff-panel"
      aria-labelledby="dashboard-rule-diff-heading"
      data-testid="dashboard-rule-diff"
      className="rounded-lg border border-brand-primary/10 bg-white p-md shadow-sm sm:p-lg"
    >
      <header className="flex items-center justify-between gap-sm">
        <h2
          id="dashboard-rule-diff-heading"
          className="font-display text-xl font-bold text-brand-primary"
        >
          {t("dashboard.ruleDiff.heading", { state: stateLabel })}
        </h2>
      </header>

      {state.kind === "loading" && (
        <div
          role="status"
          aria-live="polite"
          data-testid="dashboard-rule-diff-loading"
          className="mt-md"
        >
          {/* Low-fidelity skeleton — three stripes at body-line heights. */}
          <div className="h-3 w-3/4 animate-pulse rounded bg-brand-primary/10" />
          <div className="mt-sm h-3 w-full animate-pulse rounded bg-brand-primary/10" />
          <div className="mt-sm h-3 w-5/6 animate-pulse rounded bg-brand-primary/10" />
          <p className="mt-md text-sm text-neutral-600">
            {t("dashboard.ruleDiff.loading")}
          </p>
        </div>
      )}

      {state.kind === "preparing" && (
        <div
          role="status"
          data-testid="dashboard-rule-diff-preparing"
          className="mt-md rounded-md border border-brand-primary/10 bg-brand-surface/40 p-md text-sm text-neutral-800"
        >
          {t("dashboard.ruleDiff.preparing")}
        </div>
      )}

      {state.kind === "error" && (
        <div
          role="alert"
          data-testid="dashboard-rule-diff-error"
          className="mt-md rounded-md border border-danger/40 bg-danger/5 p-md text-sm text-danger"
        >
          <p>
            {t("dashboard.ruleDiff.errorPrefix")}: {state.message}
          </p>
          <button
            type="button"
            data-testid="dashboard-rule-diff-retry"
            onClick={retry}
            className="mt-sm inline-flex items-center rounded-md border border-danger/40 bg-white px-md py-xs text-sm font-semibold text-danger transition-colors hover:bg-danger/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-danger"
          >
            {t("dashboard.ruleDiff.retry")}
          </button>
        </div>
      )}

      {state.kind === "ok" && (
        <div className="mt-md">
          <p className="text-xs text-neutral-600">
            {t("dashboard.ruleDiff.providerLabel")}:{" "}
            {state.response.providerId} · {state.response.latencyMs}ms
          </p>
          <pre
            data-testid="dashboard-rule-diff-output"
            className="mt-sm whitespace-pre-wrap break-words rounded-md border border-brand-primary/10 bg-brand-surface/40 p-md text-sm text-neutral-900"
          >
            {state.response.diff}
          </pre>
        </div>
      )}
    </section>
  );
}
